/**
 * Pick which transcript the review page should show.
 *
 * Each recording carries its own time-aligned transcript
 * (`recordings[].transcript` from the session-content API), so selecting a
 * different segment must show THAT recording's transcript.
 *
 * The event-level transcript is a LEGACY fallback only valid for
 * single-recording sessions. In a multi-segment session there is no genuine
 * combined event transcript — the API's top-level `transcript` is just one
 * segment's (an arbitrary HasOne). Falling back to it would make a segment with
 * no transcript of its own (still ingesting, or silent) display a DIFFERENT
 * segment's words. Callers pass `allowEventFallback: false` for multi-segment
 * sessions so each segment shows only its own transcript (or nothing).
 *
 * Entries are deep-cloned so the inline transcript editor can mutate them
 * without corrupting the loaded API payload.
 *
 * @param {{ transcript?: { entries?: Array, speaker_overrides?: Object } }|null} activePart
 * @param {{ entries?: Array, speaker_overrides?: Object }|null} eventTranscript
 * @param {{ allowEventFallback?: boolean }} [options]
 * @returns {{ entries: Array, speakerOverrides: Object }}
 */
export function selectActiveTranscript(activePart, eventTranscript, { allowEventFallback = true } = {}) {
  const fallback = allowEventFallback ? eventTranscript : null;
  const transcript = activePart?.transcript ?? fallback ?? null;
  const entries = Array.isArray(transcript?.entries)
    ? transcript.entries.map((e) => ({ ...e }))
    : [];
  const speakerOverrides = transcript?.speaker_overrides ?? {};
  return { entries, speakerOverrides };
}

/**
 * The transcript object a saved edit must be written back into — the same
 * precedence as selectActiveTranscript, mirroring the backend's
 * `$recording->transcript ?? $agendaEvent->transcript`. Returns the object by
 * reference (mutate `.entries` on it) or null when no transcript exists yet.
 *
 * `allowEventFallback` must match the value passed to selectActiveTranscript so
 * a multi-segment edit never writes back into another segment's transcript.
 *
 * @param {{ transcript?: { entries?: Array } }|null} activePart
 * @param {{ entries?: Array }|null} eventTranscript
 * @param {{ allowEventFallback?: boolean }} [options]
 * @returns {{ entries?: Array }|null}
 */
export function transcriptSaveTarget(activePart, eventTranscript, { allowEventFallback = true } = {}) {
  return activePart?.transcript ?? (allowEventFallback ? eventTranscript : null) ?? null;
}
